import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '@platform/core';
import { usePlanning } from '../contexts/PlanningContext';
import { BattlePlan } from '../types/planning.types';
import CreatePlanModal from '../components/CreatePlanModal';
import { format } from 'date-fns';
import { he, enUS } from 'date-fns/locale';

export default function PlansPage() {
  const navigate = useNavigate();
  const { t, locale } = useLocale();
  const dateLocale = locale === 'he' ? he : enUS;
  const { plans, stats, loading, filter, setFilter, deletePlan } = usePlanning();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setFilter({ ...filter, searchQuery: query });
  };

  const handleStatusFilter = (status: BattlePlan['status'] | '') => {
    setFilter({ ...filter, status: status || undefined });
  };

  const handlePlanClick = (plan: BattlePlan) => {
    navigate(`/plans/${plan.id}`);
  };

  const calculateProgress = (plan: BattlePlan) => {
    let total = 0;
    let completed = 0;
    plan.subjects.forEach(subject => {
      subject.actionItems.forEach(item => {
        total++;
        if (item.isCompleted) completed++;
      });
    });
    return { total, completed, percent: total > 0 ? Math.round((completed / total) * 100) : 0 };
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t('planning.loading')}</p>
      </div>
    );
  }

  return (
    <div className="plans-page">
      <div className="page-header">
        <h1 className="page-title">{t('planning.title')}</h1>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          + {t('planning.newPlan')}
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.totalPlans}</div>
          <div className="stat-label">{t('planning.totalPlans')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--status-active)' }}>{stats.activePlans}</div>
          <div className="stat-label">{t('planning.activePlans')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--success-color)' }}>{stats.completedActionItems}</div>
          <div className="stat-label">{t('planning.completedTasks')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--error-color)' }}>{stats.overdueActionItems}</div>
          <div className="stat-label">{t('planning.overdueTasks')}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <input
          type="text"
          placeholder={t('planning.searchPlans')}
          value={searchQuery}
          onChange={handleSearch}
          className="search-input"
        />
        <div className="filter-group">
          <select
            className="form-select"
            style={{ width: 'auto' }}
            value={filter.status || ''}
            onChange={(e) => handleStatusFilter(e.target.value as BattlePlan['status'] | '')}
          >
            <option value="">{t('planning.allStatuses')}</option>
            <option value="draft">{t('planning.statusDraft')}</option>
            <option value="active">{t('planning.statusActive')}</option>
            <option value="completed">{t('planning.statusCompleted')}</option>
            <option value="archived">{t('planning.statusArchived')}</option>
          </select>
        </div>
      </div>

      {/* Plans List */}
      {plans.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <h3 className="empty-state-title">{t('planning.noPlans')}</h3>
          <p>{t('planning.noPlansDescription')}</p>
          <button
            className="btn btn-primary"
            style={{ marginTop: 'var(--spacing-lg)' }}
            onClick={() => setShowCreateModal(true)}
          >
            + {t('planning.createPlan')}
          </button>
        </div>
      ) : (
        <div className="plans-list">
          {plans.map(plan => {
            const { total, completed, percent } = calculateProgress(plan);
            return (
              <div
                key={plan.id}
                className={`plan-card status-${plan.status}`}
                onClick={() => handlePlanClick(plan)}
              >
                <div className="plan-card-header">
                  <div>
                    <h3 className="plan-title">{plan.title}</h3>
                    {plan.description && (
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '4px' }}>
                        {plan.description}
                      </p>
                    )}
                  </div>
                  <span className={`status-badge ${plan.status}`}>
                    {plan.status === 'draft' && t('planning.statusDraft')}
                    {plan.status === 'active' && t('planning.statusActive')}
                    {plan.status === 'completed' && t('planning.statusCompleted')}
                    {plan.status === 'archived' && t('planning.statusArchived')}
                  </span>
                </div>

                <div className="plan-meta">
                  <span className="plan-date">
                    📅 {format(new Date(plan.date), 'dd MMMM yyyy', { locale: dateLocale })}
                  </span>
                  <span className="plan-participants">
                    👥 {plan.participants.length} {t('planning.participants')}
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="plan-progress-section">
                  <div className="progress-header">
                    <span className="progress-label">
                      {t('planning.progressLabel').replace('{completed}', String(completed)).replace('{total}', String(total))}
                    </span>
                    <span className="progress-percent">{percent}%</span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${percent}%`,
                        backgroundColor: percent === 100 ? 'var(--success-color)' : 'var(--primary-color)'
                      }}
                    ></div>
                  </div>
                </div>

                {plan.subjects.length > 0 && (
                  <div style={{ marginTop: 'var(--spacing-md)', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    {plan.subjects.length} {t('planning.subjects')} • {total} {t('planning.actionItems')}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showCreateModal && (
        <CreatePlanModal onClose={() => setShowCreateModal(false)} />
      )}

      <style>{`
        .plan-progress-section {
          margin-top: var(--spacing-md);
        }
        .progress-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--spacing-xs);
          font-size: 0.75rem;
        }
        .progress-label {
          color: var(--text-secondary);
        }
        .progress-percent {
          font-weight: 600;
          color: var(--text-primary);
        }
        .progress-bar {
          height: 8px;
          background: var(--border-color);
          border-radius: 4px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.3s ease, background-color 0.3s ease;
        }

        @media (max-width: 768px) {
          .plans-page .page-header {
            flex-direction: column;
            gap: 0.5rem;
            align-items: stretch;
          }
          .plans-page .page-header h1 { font-size: 1.25rem; }
          .plans-page .filters-bar { flex-direction: column; gap: 0.5rem; }
          .plans-page .search-input { width: 100%; }
          .plans-page select { width: 100%; min-height: 44px; }
          .plans-page button { min-height: 44px; }
        }
      `}</style>
    </div>
  );
}
